import React, { useState } from 'react';
import { MapPin, Clock, Check } from 'lucide-react';
import { useLanguage } from '../components/LanguageContext';
import { useMeetingRequests, AVAILABLE_SLOTS } from '../components/MeetingRequestsContext';
import { MOCK_PROFILES, MockProfile } from '../data/mockProfiles';
import { ProposeTimeSheet } from '../components/ProposeTimeSheet';
type FilterType = 'all' | 'incoming' | 'accepted' | 'outgoing';
interface Props {
  onAcceptReveal: (profile: MockProfile, slotId: string) => void;
}
export function MatchConnections({
  onAcceptReveal
}: Props) {
  const {
    t
  } = useLanguage();
  const {
    requests,
    acceptRequest,
    proposeAlternative
  } = useMeetingRequests();
  const [sheetState, setSheetState] = useState<{
    mode: 'alternative' | 'reschedule';
    requestId?: string;
    profileId?: string;
  } | null>(null);
  const [filter, setFilter] = useState<FilterType>('all');
  const incoming = requests.filter(r => r.status === 'pending-incoming');
  const accepted = requests.filter(r => r.status === 'accepted');
  const outgoing = requests.filter(r => r.status === 'pending-outgoing');
  const totalCount = incoming.length + accepted.length + outgoing.length;
  const getProfile = (id: string) => MOCK_PROFILES.find(p => p.id === id) ?? null;
  const getSlotLabel = (slotId: string) => {
    const s = AVAILABLE_SLOTS.find(s => s.id === slotId);
    return s ? `${t(s.dayKey)} · ${t(s.timeKey)}` : '';
  };
  const handleSheetSubmit = (slotId: string) => {
    if (!sheetState) return;
    if (sheetState.mode === 'alternative' && sheetState.requestId) {
      proposeAlternative(sheetState.requestId, slotId);
    }
    setSheetState(null);
  };
  const chipBase = 'px-4 py-1.5 rounded-full text-[13px] font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 shrink-0';
  const chipActive = 'bg-[#C5D82D] text-[#0D0D0D] border border-[#C5D82D]';
  const chipInactive = 'border border-[#2A2A2A] text-[#888888] bg-transparent';
  return <div className="flex flex-col h-full w-full bg-[#0D0D0D] px-6 pt-2 pb-24 overflow-y-auto relative" data-id="element-412">
      {/* Filter Chips */}
      <div className="flex overflow-x-auto [&::-webkit-scrollbar]:hidden gap-2 mb-6 -mx-6 px-6 sticky top-0 z-10 bg-[#0D0D0D] py-4 shrink-0 min-h-[52px] items-center" data-id="element-413">
        <button onClick={() => setFilter('all')} className={`${chipBase} ${filter === 'all' ? chipActive : chipInactive}`} data-id="element-414">
          Tutte ({totalCount})
        </button>
        <button onClick={() => setFilter('incoming')} className={`${chipBase} ${filter === 'incoming' ? chipActive : chipInactive}`} data-id="element-415">
          In arrivo ({incoming.length})
          {incoming.length > 0 && <span className="w-1.5 h-1.5 bg-[#D32F2F] rounded-full" data-id="element-416" />}
        </button>
        <button onClick={() => setFilter('accepted')} className={`${chipBase} ${filter === 'accepted' ? chipActive : chipInactive}`} data-id="element-417">
          Confermate ({accepted.length})
        </button>
        <button onClick={() => setFilter('outgoing')} className={`${chipBase} ${filter === 'outgoing' ? chipActive : chipInactive}`} data-id="element-418">
          Inviate ({outgoing.length})
        </button>
      </div>

      {/* Incoming requests */}
      {(filter === 'all' || filter === 'incoming') && incoming.length > 0 && <div className="mb-6" data-id="element-419">
          <h3 className="text-[12px] uppercase tracking-wide text-[#6A6A6A] font-semibold mb-3" data-id="element-420">
            {t('connections.pendingHeader')}
          </h3>
          <div className="flex flex-col gap-3" data-id="element-421">
            {incoming.map(req => {
          const profile = getProfile(req.profileId);
          if (!profile) return null;
          return <div key={req.id} className="bg-[#141414] rounded-[14px] border border-[#C5D82D]/30 p-3 flex flex-col gap-3" data-id="element-422">
                  <div className="flex items-start gap-3" data-id="element-423">
                    <div className="w-10 h-10 rounded-full bg-[#2A2A2A] overflow-hidden relative shrink-0" data-id="element-424">
                      <img src={`https://randomuser.me/api/portraits/${parseInt(profile.id.replace('p', ''), 10) % 2 === 0 ? 'women' : 'men'}/${parseInt(profile.id.replace('p', ''), 10)}.jpg`} alt="" className="w-full h-full object-cover scale-110" style={{
                  filter: 'blur(3px)'
                }} data-id="element-425" />
                      <div className="absolute inset-0 bg-black/10 rounded-full" data-id="element-426" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-427">
                      <h4 className="text-[13px] text-white font-medium truncate" data-id="element-428">
                        {profile.role}
                      </h4>
                      <div className="text-[#888888] text-[11px] truncate" data-id="element-429">
                        {profile.sector}
                      </div>
                    </div>
                    <span className="text-[11px] text-[#C5D82D] font-semibold whitespace-nowrap" data-id="element-430">
                      {t('connections.wantsToMeet')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-[13px] text-[#A0A0A0]" data-id="element-431">
                    <Clock className="w-3.5 h-3.5 text-[#C5D82D]" strokeWidth={2} data-id="element-432" />
                    {getSlotLabel(req.slotId)}
                  </div>
                  <div className="flex gap-2" data-id="element-433">
                    <button onClick={() => {
                const profile = getProfile(req.profileId);
                acceptRequest(req.id);
                if (profile) {
                  onAcceptReveal(profile, req.slotId);
                }
              }} className="flex-1 py-2.5 rounded-xl bg-[#C5D82D] text-[#0D0D0D] text-[13px] font-semibold flex items-center justify-center gap-1.5 hover:bg-[#d4e63a] transition-colors" data-id="element-434">
                      <Check className="w-4 h-4" strokeWidth={2.5} data-id="element-435" />
                      {t('connections.accept')}
                    </button>
                    <button onClick={() => setSheetState({
                mode: 'alternative',
                requestId: req.id,
                profileId: profile.id
              })} className="flex-1 py-2.5 rounded-xl border border-[#2A2A2A] text-white text-[13px] font-medium hover:bg-[#1A1A1A] transition-colors" data-id="element-436">
                      {t('connections.proposeAlt')}
                    </button>
                  </div>
                </div>;
        })}
          </div>
        </div>}

      {/* Accepted matches */}
      {(filter === 'all' || filter === 'accepted') && accepted.length > 0 && <div className="mb-6" data-id="element-437">
          <h3 className="text-[12px] uppercase tracking-wide text-[#6A6A6A] font-semibold mb-3" data-id="element-438">
            {t('connections.confirmedHeader')}
          </h3>
          <div className="flex flex-col gap-4" data-id="element-439">
            {accepted.map(req => {
          const profile = getProfile(req.profileId);
          if (!profile) return null;
          return <div key={req.id} className="bg-[#141414] rounded-[14px] border border-[#2A2A2A] p-3 flex flex-col gap-3" data-id="element-440">
                  <div className="flex items-start gap-3" data-id="element-441">
                    <div className="w-10 h-10 rounded-full bg-[#2A2A2A] overflow-hidden relative shrink-0" data-id="element-442">
                      <img src={`https://randomuser.me/api/portraits/${parseInt(profile.id.replace('p', ''), 10) % 2 === 0 ? 'women' : 'men'}/${parseInt(profile.id.replace('p', ''), 10)}.jpg`} alt="" className="w-full h-full object-cover scale-110" style={{
                  filter: 'blur(3px)'
                }} data-id="element-443" />
                      <div className="absolute inset-0 bg-black/10 rounded-full" data-id="element-444" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-445">
                      <h4 className="text-[13px] text-white font-medium truncate" data-id="element-446">
                        {profile.role}
                      </h4>
                      <div className="text-[#888888] text-[11px] truncate" data-id="element-447">
                        {profile.sector}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-[13px] text-[#A0A0A0]" data-id="element-448">
                    <MapPin className="w-3.5 h-3.5 text-[#C5D82D]" strokeWidth={2} data-id="element-449" />
                    {getSlotLabel(req.slotId)}
                  </div>
                  <button onClick={() => setSheetState({
              mode: 'reschedule',
              requestId: req.id,
              profileId: profile.id
            })} className="self-start flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#2A2A2A] text-white text-[13px] font-medium hover:bg-[#1A1A1A] transition-colors" data-id="element-450">
                    <MapPin className="w-4 h-4" strokeWidth={2} data-id="element-451" />
                    {t('connections.meet')}
                  </button>
                </div>;
        })}
          </div>
        </div>}

      {/* Outgoing pending */}
      {(filter === 'all' || filter === 'outgoing') && outgoing.length > 0 && <div className="mb-6" data-id="element-452">
          <h3 className="text-[12px] uppercase tracking-wide text-[#6A6A6A] font-semibold mb-3" data-id="element-453">
            {t('connections.sentHeader')}
          </h3>
          <div className="flex flex-col gap-3" data-id="element-454">
            {outgoing.map(req => {
          const profile = getProfile(req.profileId);
          if (!profile) return null;
          return <div key={req.id} className="bg-[#141414] rounded-[14px] border border-[#2A2A2A] p-3 flex items-center gap-3" data-id="element-455">
                  <div className="w-10 h-10 rounded-full bg-[#2A2A2A] overflow-hidden relative shrink-0" data-id="element-456">
                    <img src={`https://randomuser.me/api/portraits/${parseInt(profile.id.replace('p', ''), 10) % 2 === 0 ? 'women' : 'men'}/${parseInt(profile.id.replace('p', ''), 10)}.jpg`} alt="" className="w-full h-full object-cover scale-110" style={{
                filter: 'blur(3px)'
              }} data-id="element-457" />
                    <div className="absolute inset-0 bg-black/10 rounded-full" data-id="element-458" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-459">
                    <h4 className="text-[13px] text-white font-medium truncate" data-id="element-460">
                      {profile.role}
                    </h4>
                    <div className="text-[#888888] text-[11px] truncate" data-id="element-461">
                      {profile.sector}
                    </div>
                  </div>
                  <span className="text-[11px] text-[#6A6A6A] font-medium" data-id="element-462">
                    {t('connections.waiting')}
                  </span>
                </div>;
        })}
          </div>
        </div>}

      {/* Empty State */}
      {(filter === 'all' && totalCount === 0 || filter === 'incoming' && incoming.length === 0 || filter === 'accepted' && accepted.length === 0 || filter === 'outgoing' && outgoing.length === 0) && <div className="flex-1 flex flex-col items-center justify-center text-center py-12" data-id="element-463">
          <p className="text-[14px] text-[#888888]" data-id="element-464">{t('connections.empty')}</p>
        </div>}

      <ProposeTimeSheet isOpen={sheetState !== null} title={sheetState?.mode === 'alternative' ? t('meeting.proposeAltTitle') : t('meeting.rescheduleTitle')} onClose={() => setSheetState(null)} onSubmit={handleSheetSubmit} submitLabelKey={sheetState?.mode === 'alternative' ? 'meeting.sendAlternative' : 'meeting.send'} data-id="element-465" />
    </div>;
}